import { dictionaryApi } from './dictionaryClient';

/** API spellings (including `verifiedwtice`). */
export const STAT_STATUS_VALUES = [
  'notverified',
  'verifiedonce',
  'verifiedwtice',
  'verifiedthrice',
] as const;

export type StatStatus = (typeof STAT_STATUS_VALUES)[number];

export type DictionaryStat = {
  id: number;
  subantaId?: number | null;
  tingantaId?: number | null;
  status: StatStatus;
  published: boolean;
  ruchi: number;
};

export type CreateStatsBody =
  | {
      subantaId: number;
      status?: StatStatus;
      published?: boolean;
      ruchi?: number;
    }
  | {
      tingantaId: number;
      status?: StatStatus;
      published?: boolean;
      ruchi?: number;
    };

export type PatchStatsBody = Partial<Pick<DictionaryStat, 'status' | 'published' | 'ruchi'>>;

function normalizeList(data: unknown): DictionaryStat[] {
  if (Array.isArray(data)) return data as DictionaryStat[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: DictionaryStat[] }).data;
  }
  return [];
}

function isStatStatus(x: unknown): x is StatStatus {
  return typeof x === 'string' && (STAT_STATUS_VALUES as readonly string[]).includes(x);
}

export function isDictionaryStat(x: unknown): x is DictionaryStat {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.published === 'boolean' &&
    typeof o.ruchi === 'number' &&
    isStatStatus(o.status)
  );
}

export function parseDictionaryStats(raw: unknown[] | undefined): DictionaryStat[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isDictionaryStat);
}

export async function listStats(): Promise<DictionaryStat[]> {
  const res = await dictionaryApi.get<unknown>('/stats');
  return normalizeList(res.data);
}

export async function getStat(id: number): Promise<DictionaryStat> {
  const res = await dictionaryApi.get<DictionaryStat>(`/stats/${id}`);
  return res.data;
}

export async function createStat(body: CreateStatsBody): Promise<DictionaryStat> {
  const res = await dictionaryApi.post<DictionaryStat>('/stats', body);
  return res.data;
}

export async function updateStat(id: number, body: PatchStatsBody): Promise<DictionaryStat> {
  const res = await dictionaryApi.patch<DictionaryStat>(`/stats/${id}`, body);
  return res.data;
}

export async function deleteStat(id: number): Promise<void> {
  await dictionaryApi.delete(`/stats/${id}`);
}

export function statBelongsToEntry(
  s: DictionaryStat,
  variant: 'subanta' | 'tinganta',
  parentId: number
): boolean {
  if (variant === 'subanta') return s.subantaId === parentId;
  return s.tingantaId === parentId;
}

function statStatusLabel(v: StatStatus): string {
  switch (v) {
    case 'notverified':
      return 'Not verified';
    case 'verifiedonce':
      return 'Verified once';
    case 'verifiedwtice':
      return 'Verified twice';
    case 'verifiedthrice':
      return 'Verified thrice';
    default:
      return v;
  }
}

export const STAT_STATUS_SELECT_OPTIONS = STAT_STATUS_VALUES.map((value) => ({
  value,
  label: statStatusLabel(value),
}));
